import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useInvestorAuth } from '../../context/InvestorAuthContext';
import { useIsMobile } from '../../utils/useIsMobile';
import { Icon } from '../../utils/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SIDEBAR_WIDTH = 240;
const TOPBAR_HEIGHT = 56;
const PRIMARY = '#3a6ad9';

const NAV_ITEMS = [
  { label: 'Dashboard',       path: '/market/portal/dashboard',  iconName: 'home' },
  { label: 'Available Pools', path: '/market/portal/pools',      iconName: 'chart' },
  { label: 'My Bids',         path: '/market/portal/bids',       iconName: 'list' },
  { label: 'Messages',        path: '/market/portal/messages',   iconName: 'chat',     badge: 'messages' },
  { label: 'Alerts',          path: '/market/portal/alerts',     iconName: 'bell',     badge: 'alerts' },
  { label: 'Resources',       path: '/market/portal/resources',  iconName: 'book' },
  { label: 'My Documents',    path: '/market/portal/documents',  iconName: 'folder' },
];

const Footer = () => (
  <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: PRIMARY, marginBottom: 6 }}>
      DoorlyMarket | Investor Portal
    </div>
    <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.7 }}>
      <div>Go Doorly, LLC</div>
      <div>NMLS #2764749</div>
      <div>Equal Housing Opportunity</div>
      <div>8708 S Congress Ave, Ste E500-202</div>
      <div>Austin, TX 78745</div>
      {/* The year from the clock, never typed (it showed last year into 2026). */}
      <div style={{ marginTop: 6 }}>© {new Date().getFullYear()} Go Doorly, LLC</div>
    </div>
  </div>
);

export default function InvestorPortalNav({ children }) {
  const { investor, token, investorLogout } = useInvestorAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  useEffect(() => { document.title = 'DoorlyMarket | Investor Portal'; }, []);

  const fetchCounts = useCallback(async () => {
    if (!token) return;
    try {
      const [m, a] = await Promise.all([
        axios.get(`${API_URL}/api/market/investor/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API_URL}/api/market/investor/alerts/unread-count`,   { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } })),
      ]);
      setUnreadMessages(m.data?.count || 0);
      setUnreadAlerts(a.data?.count || 0);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => clearInterval(id);
  }, [token, fetchCounts, location.pathname]);

  const investorName = investor
    ? [investor.firstName, investor.lastName].filter(Boolean).join(' ') || investor.email || 'Investor'
    : 'Investor';

  const badgeFor = (key) => key === 'messages' ? unreadMessages : key === 'alerts' ? unreadAlerts : 0;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'inherit', overflowX: 'hidden', maxWidth: '100%', width: '100%' }}>

      {/* ── Desktop Sidebar ── */}
      {!isMobile && (
      <aside style={{
        width: SIDEBAR_WIDTH, minHeight: '100vh', backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb', position: 'fixed', top: 0, left: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', zIndex: 100,
      }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <Link to="/market/portal/dashboard" style={{ display: 'inline-block' }}>
            <img src="/doorly-market-logo.png" alt="DoorlyMarket"
              style={{ height: 44, width: 'auto', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
            <span style={{ display: 'none', fontSize: 22, fontWeight: 800, color: PRIMARY, letterSpacing: '-0.5px' }}>
              Doorly<span style={{ color: '#111827' }}>Market</span>
            </span>
          </Link>
        </div>
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.path || (item.path !== '/market/portal/dashboard' && location.pathname.startsWith(item.path + '/'));
            const count = badgeFor(item.badge);
            return (
              <Link key={item.path} to={item.path}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                  borderRadius: 8, marginBottom: 2, textDecoration: 'none',
                  fontSize: 13.5, fontWeight: active ? 600 : 400,
                  color: active ? PRIMARY : '#374151',
                  backgroundColor: active ? '#eff3fe' : 'transparent',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#111827'; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; } }}
              >
                <span style={{ width: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: active ? PRIMARY : '#6b7280' }}>
                  <Icon name={item.iconName} size={17} />
                </span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {count > 0 && (
                  <span style={{ background: '#ff082f', color: '#fff', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <Footer />
      </aside>
      )}

      {/* ── Main area ── */}
      <div style={{ marginLeft: isMobile ? 0 : SIDEBAR_WIDTH, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0, overflowX: 'hidden' }}>

        {/* Topbar */}
        <header style={{
          height: TOPBAR_HEIGHT, backgroundColor: isMobile ? PRIMARY : '#ffffff',
          borderBottom: isMobile ? 'none' : '1px solid #e5e7eb',
          position: 'sticky', top: 0, zIndex: 90, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: isMobile ? '0 16px' : '0 24px',
          minWidth: 0, width: '100%', boxSizing: 'border-box',
        }}>
          {isMobile ? (
            <>
              <Link to="/market/portal/dashboard" style={{ display: 'flex', alignItems: 'center' }}>
                <img src="/doorly-market-logo.png" alt="DoorlyMarket"
                  style={{ height: 24, width: 'auto', filter: 'brightness(0) invert(1)' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => navigate('/market/portal/alerts')} aria-label="Alerts"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, position: 'relative' }}>
                  <Icon name="bell" size={22} color="#fff" />
                  {unreadAlerts > 0 && (
                    <span style={{ position: 'absolute', top: 4, right: 4, background: '#ff082f', color: '#fff', fontSize: 9, fontWeight: 800, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #3a6ad9' }}>
                      {unreadAlerts > 9 ? '9+' : unreadAlerts}
                    </span>
                  )}
                </button>
                <button onClick={() => setMenuOpen(true)} aria-label="Open menu"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ width: 22, height: 2, background: '#fff', borderRadius: 2 }} />
                  <div style={{ width: 22, height: 2, background: '#fff', borderRadius: 2 }} />
                  <div style={{ width: 22, height: 2, background: '#fff', borderRadius: 2 }} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Welcome, <span style={{ fontWeight: 600, color: '#111827' }}>{investorName}</span>
                {investor?.firmName ? <span style={{ color: '#9ca3af' }}> · {investor.firmName}</span> : null}
              </div>
              <button onClick={investorLogout}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'transparent', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon name="logout" size={15} /> Sign out
              </button>
            </>
          )}
        </header>

        {/* Page content */}
        <main style={{ flex: 1, minWidth: 0, backgroundColor: '#f9fafb', padding: isMobile ? '16px 14px 24px' : '28px', boxSizing: 'border-box', overflowX: 'hidden', maxWidth: '100%', width: '100%' }}>
          {children}
        </main>
      </div>

      {/* ── Mobile Nav Drawer ── */}
      {isMobile && menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 280, background: '#fff', zIndex: 301, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)' }}>
            <div style={{ background: PRIMARY, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{investorName}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                  {investor?.firmName || 'Investor'}
                </div>
              </div>
              <button onClick={() => setMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: 22, lineHeight: 1, padding: 4 }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
              {NAV_ITEMS.map((item) => {
                const active = location.pathname === item.path;
                const count = badgeFor(item.badge);
                return (
                  <button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', background: active ? '#eff6ff' : 'none', border: 'none', borderLeft: `3px solid ${active ? PRIMARY : 'transparent'}`, cursor: 'pointer', textAlign: 'left' }}>
                    <Icon name={item.iconName} size={18} color={active ? PRIMARY : '#6b7280'} />
                    <span style={{ fontSize: 15, fontWeight: active ? 700 : 500, color: active ? PRIMARY : '#374151', flex: 1 }}>{item.label}</span>
                    {count > 0 && (
                      <span style={{ background: '#ff082f', color: '#fff', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
              <button onClick={() => { investorLogout(); setMenuOpen(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 10, cursor: 'pointer', color: '#ff082f', fontWeight: 600, fontSize: 14 }}>
                <Icon name="logout" size={18} color="#ff082f" /> Sign Out
              </button>
              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 10, color: '#d1d5db' }}>
                Go Doorly, LLC | NMLS #2764749
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
